import React, { forwardRef, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Decimation,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import useChartZoom, { applyZoomToOptions } from './useChartZoom';
import ChartZoomBar from './ChartZoomBar';
import './styling/LineChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  // Registered, not enabled: a caller opts in per chart through
  // customOptions.plugins.decimation (KineticsFittingPage does, for
  // day-by-day lab programs).
  Decimation
);

/*
  LineChart Component
  Purpose: Reusable line chart component for time-series and continuous data visualization.
  Props:
    - title: string (chart title)
    - data: { labels: Array, datasets: Array } (Chart.js data format)
    - xAxisLabel: string (x-axis title)
    - yAxisLabel: string (y-axis title)
    - height: string (CSS height value, default: '100%')
    - showLegend: boolean (show/hide legend, default: true)
    - legendPosition: 'top' | 'bottom' | 'left' | 'right' (default: 'top')
    - customOptions: object (additional Chart.js options to merge)
    - plugins: Array (custom Chart.js plugins)
    - loading: boolean (show loading state)
    - error: string (error message to display)
    - emptyMessage: string (message when no data)
    - zoomable: boolean (default false) — x-axis zoom: drag / Shift + scroll /
      Shift + drag pan / double-click reset, with a hint + "Reset zoom" bar
      under the chart. Read once when the chart is created.
    - zoom: a useChartZoom() controller shared by several charts on the same x
      axis (stacked panels). Overrides zoomable; the caller renders one
      ChartZoomBar for the group.
    - zoomResetKey: any value — zoom resets when it changes. Defaults to a
      signature of the x extent (label count + first/last label, or first/last
      x), so toggling series keeps the zoom but new data resets it.
    - zoomMinRange: number (default 2) — narrowest window in x-scale units
    - zoomLabel: (xValue) => string — label for the "Showing … – …" line.
      Defaults to the category label at that index, else the rounded value.
  Ref:
    - Forwards a ref to the underlying Chart.js instance, so callers can drive
      cheap imperative repaints (chart.render()) or read scales/chartArea without
      triggering a full React-driven chart.update().
  Behavior:
    - Responsive chart that adapts to container size
    - Dark theme styling consistent with app design
    - Loading and error states
    - Supports all Chart.js dataset configurations
    - chartOptions is memoized so a stable customOptions reference does not force a
      chart update on every parent re-render.
  Edge Cases:
    - Empty datasets array: shows empty state
    - Missing labels: falls back to empty array
    - Invalid data: shows error state
*/
const LineChart = forwardRef(({
  title = '',
  data = { labels: [], datasets: [] },
  xAxisLabel = '',
  yAxisLabel = '',
  height = '100%',
  showLegend = true,
  legendPosition = 'top',
  customOptions = {},
  plugins = [],
  loading = false,
  error = null,
  emptyMessage = 'No data available',
  zoomable = false,
  zoom: sharedZoom = null,
  zoomResetKey,
  zoomMinRange = 2,
  zoomLabel,
}, ref) => {
  // Zoom — hooks run before the loading/error/empty early returns.
  const ownZoom = useChartZoom({
    resetKey: zoomResetKey !== undefined ? zoomResetKey : xExtentSignature(data),
    enabled: zoomable && !sharedZoom,
    minRange: zoomMinRange,
  });
  const zoom = sharedZoom || (zoomable ? ownZoom : null);
  const allPlugins = useMemo(
    () => (zoom ? [...plugins, zoom.plugin] : plugins),
    [plugins, zoom?.plugin] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Merge custom options with defaults. Memoized so that, when the caller passes a
  // stable customOptions reference, we hand react-chartjs-2 the same options object
  // across renders — which means no chart.update() fires on an unrelated parent
  // re-render (e.g. a playback cursor moving via an imperative chart.render()).
  const chartOptions = useMemo(() => {
    const defaultOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: showLegend,
          position: legendPosition,
          labels: {
            color: '#e2e8f0',
            font: {
              family: "'Inter', sans-serif",
              size: 12
            },
            padding: 15,
            usePointStyle: true
          }
        },
        title: {
          display: !!title,
          text: title,
          color: '#e2e8f0',
          font: {
            family: "'Inter', sans-serif",
            size: 16,
            weight: '600'
          },
          padding: {
            top: 10,
            bottom: 20
          }
        },
        tooltip: {
          backgroundColor: 'rgba(30, 41, 59, 0.95)',
          titleColor: '#e2e8f0',
          bodyColor: '#e2e8f0',
          borderColor: '#4c1d95',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (context) => {
              return context[0].label || '';
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: !!xAxisLabel,
            text: xAxisLabel,
            color: '#e2e8f0',
            font: {
              family: "'Inter', sans-serif",
              size: 13,
              weight: '500'
            }
          },
          ticks: {
            color: '#e2e8f0',
            font: {
              family: "'Inter', sans-serif",
              size: 11
            }
          },
          grid: {
            color: '#374151',
            drawBorder: false
          }
        },
        y: {
          title: {
            display: !!yAxisLabel,
            text: yAxisLabel,
            color: '#e2e8f0',
            font: {
              family: "'Inter', sans-serif",
              size: 13,
              weight: '500'
            }
          },
          ticks: {
            color: '#e2e8f0',
            font: {
              family: "'Inter', sans-serif",
              size: 11
            }
          },
          grid: {
            color: '#374151',
            drawBorder: false
          }
        }
      },
      interaction: {
        mode: 'index',
        intersect: false
      }
    };

    return {
      ...defaultOptions,
      ...customOptions,
      plugins: {
        ...defaultOptions.plugins,
        ...(customOptions.plugins || {})
      },
      scales: {
        ...defaultOptions.scales,
        ...(customOptions.scales || {})
      }
    };
  }, [title, xAxisLabel, yAxisLabel, showLegend, legendPosition, customOptions]);

  const zoomRange = zoom?.range ?? null;
  const zoomedOptions = useMemo(
    () => applyZoomToOptions(chartOptions, zoomRange),
    [chartOptions, zoomRange]
  );

  // Loading state
  if (loading) {
    return (
      <div className="line-chart" style={{ height }}>
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading chart data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="line-chart" style={{ height }}>
        <div className="chart-error">
          <span className="error-icon">⚠️</span>
          <h4>Error Loading Chart</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data.datasets || data.datasets.length === 0) {
    return (
      <div className="line-chart" style={{ height }}>
        <div className="chart-empty">
          <span className="empty-icon">📊</span>
          <h4>No Data Available</h4>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="line-chart" style={{ height }}>
      <div className="line-chart-container">
        <Line ref={ref} data={data} options={zoomedOptions} plugins={allPlugins} />
      </div>
      {zoomable && !sharedZoom && (
        <ChartZoomBar
          zoom={ownZoom}
          formatValue={zoomLabel || ((v) => defaultZoomLabel(data, v))}
        />
      )}
    </div>
  );
});

LineChart.displayName = 'LineChart';

/** Cheap string identity for the x extent — new data resets the zoom, a
 *  series toggle over the same dates doesn't. Callers often rebuild `data`
 *  every render, so object identity can't be the key. */
function xExtentSignature(data) {
  const labels = data?.labels;
  if (labels?.length) return `L${labels.length}|${String(labels[0])}|${String(labels[labels.length - 1])}`;
  let lo = Infinity;
  let hi = -Infinity;
  (data?.datasets || []).forEach((ds) => {
    const pts = ds?.data || [];
    const first = pts[0]?.x;
    const last = pts[pts.length - 1]?.x;
    if (typeof first === 'number') lo = Math.min(lo, first);
    if (typeof last === 'number') hi = Math.max(hi, last);
  });
  return `X${lo}|${hi}`;
}

function defaultZoomLabel(data, v) {
  const label = data?.labels?.[Math.round(v)];
  if (label != null && label !== '') {
    return label instanceof Date ? label.toLocaleDateString() : String(label);
  }
  return Math.round(v).toLocaleString();
}

export default LineChart;
